import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Tooltip, Input } from 'antd';
import {
  BoldOutlined, ItalicOutlined, UnderlineOutlined, OrderedListOutlined, UnorderedListOutlined,
  AlignLeftOutlined, AlignCenterOutlined, AlignRightOutlined, LinkOutlined, DisconnectOutlined,
  ClearOutlined, CodeOutlined, FontSizeOutlined,
} from '@ant-design/icons';

// Editor de texto com formatacao (negrito, listas, links...), para quem nao escreve HTML.
// Por baixo continua gravando HTML, entao templates antigos abrem formatados e o envio
// de e-mail nao muda. Sem biblioteca externa: contentEditable + execCommand, que todos
// os navegadores suportam. Funciona dentro de <Form.Item> (value/onChange) e expoe
// ref.inserir(texto) para colocar uma variavel onde o cursor estiver.

const Sep = () => <span style={{ width: 1, height: 18, background: '#e5e5e5', margin: '0 4px' }} />;
const CORES = ['#333333', '#098fdd', '#1a3c5e', '#389e0d', '#cf1322', '#d46b08'];

const EditorTexto = forwardRef(function EditorTexto({ value, onChange, altura = 260 }, ref) {
  const areaRef = useRef(null);
  const selecao = useRef(null);
  const [modoHtml, setModoHtml] = useState(false);

  // so reescreve o conteudo quando ele mudou por fora (abrir outro template),
  // senao o cursor pularia para o inicio a cada letra
  useEffect(() => {
    const el = areaRef.current;
    if (el && !modoHtml && el.innerHTML !== (value || '')) el.innerHTML = value || '';
  }, [value, modoHtml]);

  const avisar = () => { if (areaRef.current && onChange) onChange(areaRef.current.innerHTML); };

  const guardarSelecao = () => {
    const s = window.getSelection();
    if (s && s.rangeCount && areaRef.current && areaRef.current.contains(s.anchorNode)) selecao.current = s.getRangeAt(0).cloneRange();
  };
  const restaurarSelecao = () => {
    const el = areaRef.current;
    if (!el) return;
    el.focus();
    const s = window.getSelection();
    if (selecao.current) { s.removeAllRanges(); s.addRange(selecao.current); }
    else { const r = document.createRange(); r.selectNodeContents(el); r.collapse(false); s.removeAllRanges(); s.addRange(r); }
  };

  const cmd = (comando, arg) => {
    restaurarSelecao();
    document.execCommand(comando, false, arg);
    guardarSelecao();
    avisar();
  };

  useImperativeHandle(ref, () => ({
    inserir: (texto) => {
      if (modoHtml) { onChange && onChange((value || '') + texto); return; }
      cmd('insertText', texto);
    },
  }));

  const link = () => {
    restaurarSelecao();
    const url = window.prompt('Endereço do link (ex.: https://www.celum.com.br)', 'https://');
    if (!url || url === 'https://') return;
    cmd('createLink', /^(https?:|mailto:|tel:)/i.test(url) ? url : 'https://' + url);
  };

  // colar sempre como texto simples: o que vem do Word/WhatsApp traz estilos que
  // quebram o e-mail. A formatacao se refaz pelos botoes.
  const colar = (e) => {
    e.preventDefault();
    const t = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, t);
    avisar();
  };

  const B = ({ titulo, icone, onClick, children }) => (
    <Tooltip title={titulo}>
      <Button size="small" type="text" icon={icone} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>{children}</Button>
    </Tooltip>
  );

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, background: '#fff' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, padding: '4px 6px', borderBottom: '1px solid #f0f0f0', background: '#fafafa', borderRadius: '6px 6px 0 0' }}>
        {!modoHtml && (<>
          <B titulo="Negrito (Ctrl+B)" icone={<BoldOutlined />} onClick={() => cmd('bold')} />
          <B titulo="Itálico (Ctrl+I)" icone={<ItalicOutlined />} onClick={() => cmd('italic')} />
          <B titulo="Sublinhado (Ctrl+U)" icone={<UnderlineOutlined />} onClick={() => cmd('underline')} />
          <Sep />
          <B titulo="Título" icone={<FontSizeOutlined />} onClick={() => cmd('formatBlock', '<h3>')} />
          <B titulo="Texto normal" onClick={() => cmd('formatBlock', '<p>')}>¶</B>
          <Sep />
          <B titulo="Lista com marcadores" icone={<UnorderedListOutlined />} onClick={() => cmd('insertUnorderedList')} />
          <B titulo="Lista numerada" icone={<OrderedListOutlined />} onClick={() => cmd('insertOrderedList')} />
          <Sep />
          <B titulo="Alinhar à esquerda" icone={<AlignLeftOutlined />} onClick={() => cmd('justifyLeft')} />
          <B titulo="Centralizar" icone={<AlignCenterOutlined />} onClick={() => cmd('justifyCenter')} />
          <B titulo="Alinhar à direita" icone={<AlignRightOutlined />} onClick={() => cmd('justifyRight')} />
          <Sep />
          <B titulo="Inserir link" icone={<LinkOutlined />} onClick={link} />
          <B titulo="Remover link" icone={<DisconnectOutlined />} onClick={() => cmd('unlink')} />
          <Sep />
          {CORES.map((c) => (
            <Tooltip key={c} title="Cor do texto">
              <span onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('foreColor', c)}
                style={{ display: 'inline-block', width: 16, height: 16, borderRadius: 3, background: c, cursor: 'pointer', margin: '0 2px', border: '1px solid #ddd' }} />
            </Tooltip>
          ))}
          <Sep />
          <B titulo="Limpar formatação da seleção" icone={<ClearOutlined />} onClick={() => cmd('removeFormat')} />
        </>)}
        <span style={{ flex: 1 }} />
        <B titulo={modoHtml ? 'Voltar ao editor' : 'Ver/editar o código HTML (avançado)'} icone={<CodeOutlined />}
          onClick={() => setModoHtml(!modoHtml)}>{modoHtml ? 'Editor' : 'HTML'}</B>
      </div>
      {modoHtml ? (
        <Input.TextArea value={value} onChange={(e) => onChange && onChange(e.target.value)} variant="borderless"
          style={{ fontFamily: 'monospace', fontSize: 12, minHeight: altura }} />
      ) : (
        <div ref={areaRef} contentEditable suppressContentEditableWarning
          onInput={avisar} onBlur={() => { guardarSelecao(); avisar(); }} onKeyUp={guardarSelecao} onMouseUp={guardarSelecao}
          onPaste={colar}
          style={{ minHeight: altura, maxHeight: 520, overflowY: 'auto', padding: '12px 14px', outline: 'none', fontSize: 14, lineHeight: 1.6, color: '#333' }} />
      )}
    </div>
  );
});

export default EditorTexto;
